<template>
  <div
    ref="fileWrapper"
    class="file-wrapper"
  >
    <BasicCheckbox
      v-if="canShowDeleted"
      label="Show Deleted"
      :value="showDeletedFilesId"
      :modelValue="withDeleted"
      class="create-item__right__show-deleted-files"
      checkType="switch"
      @update:modelValue="showDeleted"
    />
    <div
      v-if="isContentExist"
      class="file-wrapper__list"
      :class="{
        'is-compact': isDropVisible && isCompact
      }"
    >
      <div class="file-wrapper__in">
        <Draggable
          :list="displayedFiles"
          class="draggable__area"
          :group="{ name: 'files', pull: 'clone', put: false }"
          :move="checkMove"
          ghostClass="is-file-choosed"
          dragClass="is-file-drag"
          :setData="onDraggableSetData"
          @start="onDraggableStart"
          @end="onDraggableEnd"
        >
          <template #item="{ element }">
            <FileItem
              :id="element.id"
              :key="element.id"
              :file="element"
              fancyboxGroup="file_tab_fancybox"
              :files="displayedFiles"
              :progress="uploadProgress[element.id] ?? 0"
              :isUploading="element.id in uploadProgress"
              :class="{ 'pointer-none': element.id in uploadProgress }"
              :hideMenu="isReadonly"
              :projectScoped="projectScoped"
              fileMarkup="line"
              @deleteFile="deleteFile"
            />
          </template>
        </Draggable>

        <ObserverComponent
          :last="last"
          @intersect="scroll"
          @visibilityChange="onScrollEndVisibilityChange"
        />
      </div>
      <div
        v-if="isScrollable"
        class="content__box-shadow"
      />
    </div>

    <div
      v-if="isDropVisible"
      class="file-wrapper__drop"
      :class="{ 'is-drop-active': uploadButton?.dropActive, 'is-compact': isCompact }"
    >
      <file-upload
        :inputId="uploadInputId"
        ref="uploadButton"
        :multiple="true"
        :drop="true"
        :dropDirectory="true"
        :size="1048576"
        :modelValue="newFiles"
        class="file-wrapper__drop-upload"
        @update:modelValue="updatedFiles"
        @click="onClick"
      >
        <div
          ref="dropContent"
          class="file-wrapper__drop-content"
        >
          <span
            v-if="!isCompact"
            class="file-wrapper__drop-image"
          >
            <icon
              :data="uploadImage"
              original
              :fill="false"
              width="100%"
              height="100%"
              class="file-wrapper__drop-image-icon"
              style="stroke: none"
            />
          </span>
          <BaseSubHeading
            weight="500"
            size="large"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            Drag Files Here
          </BaseSubHeading>
          <span class="file-wrapper__drop-divider">or</span>
          <button
            type="button"
            class="file-wrapper__drop-button"
          >
            Upload a File
          </button>
          <BaseSubHeading
            size="small"
            weight="500"
            color="monochrome-06"
            className="file-wrapper__drop-limit"
          >
            up to {{ store.getters[Getters.GET_MAX_UPLOAD_SIZE_LABEL].toUpperCase() }} each
          </BaseSubHeading>
        </div>
      </file-upload>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref, toRefs, watch } from "vue"
import Draggable from "vuedraggable"
import { useStore } from "vuex"

import FileItem from "@/components/File/FileItem.vue"
import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"
import ObserverComponent from "@/components/Observer/ObserverComponent.vue"

import { FileMetaData, VUFile } from "@/models"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { useWait } from "@/plugins/vuewait"
import { Actions, Getters } from "@/store"
import { shakeAnimation } from "@/utils/gsapAnimation"

interface FileListProps {
  files: FileMetaData[]
  last?: boolean
  withDeleted?: boolean
  isReadonly?: boolean
  canShowDeleted?: boolean
  /** Key under which the file store keeps this tab's uploads; they are shown above the loaded files. */
  uploadListKey?: string
  /** Set false when file previews belong to an account-scoped resource. */
  projectScoped?: boolean
  /** File deep link to reveal after the paginated list is rendered. */
  scrollToFile?: string
  /** Loads the next page while resolving a file deep link. */
  loadMore?: () => Promise<unknown>
}

interface UploadButtonRef {
  clear: () => void
  dropActive?: boolean
}

const props = withDefaults(defineProps<FileListProps>(), {
  last: false,
  withDeleted: false,
  isReadonly: false,
  canShowDeleted: true,
  projectScoped: true,
  scrollToFile: "",
  loadMore: undefined,
  uploadListKey: ""
})

const emit = defineEmits<{
  (event: "deleteFile", payload: FileMetaData): void
  (event: "upload", payload: VUFile[]): void
  (event: "showDeleted", payload: boolean): void
  (event: "onScroll"): void
  (event: "scrollToFileHandled", payload: { file: FileMetaData; files: FileMetaData[] }): void
}>()

const {
  files,
  last,
  withDeleted,
  isReadonly,
  canShowDeleted,
  uploadListKey,
  projectScoped,
  scrollToFile,
  loadMore
} = toRefs(props)

const store = useStore()
const wait = useWait()
/** Keeps each label bound to its own switch when Item and ToDo Files tabs are open together. */
const showDeletedFilesId = `showDeletedFiles-${getCurrentInstance()?.uid}`

const newFiles = ref<VUFile[]>([])
const uploadButton = ref<UploadButtonRef | null>(null)
const uploadInputId = `file-list-upload-${getCurrentInstance()?.uid}`
const fileWrapper = ref<HTMLElement | null>(null)
const dropContent = ref<HTMLElement | null>(null)
const isCompact = ref(false)
const isScrollable = ref(false)
const hasPendingDeletedFiles = ref(false)

const isDark = computed(() => store.getters[Getters.THEME_IS_DARK] as boolean)
const uploadingFiles = computed<FileMetaData[]>(() =>
  uploadListKey.value ? store.getters[Getters.GET_UPLOADING_FILES](uploadListKey.value) : []
)
const uploadProgress = computed<Record<string, number>>(
  () => store.getters[Getters.GET_UPLOAD_PROGRESS]
)
/** Uploads come first; a file both uploaded and already loaded from the server is shown once. */
const displayedFiles = computed(() => {
  const loadedIds = new Set(files.value.map(file => file.id))
  return [...uploadingFiles.value.filter(file => !loadedIds.has(file.id)), ...files.value]
})
const isContentExist = computed(() => displayedFiles.value.length > 0)
const isDropVisible = computed(
  () => !isReadonly.value && !withDeleted.value && !hasPendingDeletedFiles.value
)
const uploadImage = computed(() =>
  isDark.value ? require("@icon/upload-image-dark.svg") : require("@icon/upload-image.svg")
)

const updatePendingDeletedFiles = () => {
  hasPendingDeletedFiles.value = !withDeleted.value && files.value.some(file => file.deleted)
}

const checkCompact = async () => {
  if (!isDropVisible.value || !isContentExist.value) {
    isCompact.value = false
    return
  }

  isCompact.value = false
  await nextTick()

  if (!fileWrapper.value || !dropContent.value) return

  const parentRect = fileWrapper.value.getBoundingClientRect()
  const contentRect = dropContent.value.getBoundingClientRect()

  isCompact.value = contentRect.bottom > parentRect.bottom || contentRect.top < parentRect.top
}

const onScrollEndVisibilityChange = (visible: boolean) => {
  isScrollable.value = !visible
}

watch([files, () => displayedFiles.value.length], ([, newLength], [, oldLength]) => {
  updatePendingDeletedFiles()
  if (isCompact.value && newLength > oldLength) return
  checkCompact()
})

watch([() => files.value.map(file => file.id), uploadingFiles], () => {
  const loadedIds = new Set(files.value.map(file => file.id))
  const loadedUploads = uploadingFiles.value.filter(file => loadedIds.has(file.id))
  if (!loadedUploads.length) return

  store.dispatch(Actions.CLEAR_UPLOADED_FILES, {
    uploadListKey: uploadListKey.value,
    ids: loadedUploads.map(file => file.id)
  })
})

/** Finds a deep-linked file in the loaded pages or requests the next 20 until it appears. */
const scrollToRequestedFile = async () => {
  const fileId = scrollToFile.value
  if (!fileId || !fileWrapper.value) return

  const file = files.value.find(item => item.id === fileId)
  if (!file) {
    if (!last.value && loadMore.value) {
      await loadMore.value()
      await scrollToRequestedFile()
    }
    return
  }

  await checkCompact()
  await nextTick()

  const target = fileWrapper.value.querySelector<HTMLElement>(`#${CSS.escape(fileId)}`)
  const scrollContainer = fileWrapper.value.querySelector<HTMLElement>(".file-wrapper__in")
  if (!target || !scrollContainer) return

  await new Promise<void>(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
  scrollContainer.scrollTo({ top: target.offsetTop, behavior: "smooth" })
  await shakeAnimation(target, 0.7)
  emit("scrollToFileHandled", { file, files: files.value })
}

onMounted(() => {
  updatePendingDeletedFiles()
  // Files are usually loaded before the tab opens, so the files watcher never fires here.
  checkCompact()
  scrollToRequestedFile()
})

const onDraggableSetData = (dataTransfer: DataTransfer, dragEl: HTMLElement) => {
  const file = files.value.find(item => item.id === dragEl.getAttribute("id"))

  if (!file) return

  dataTransfer.setData(ReferenceType.FILE, JSON.stringify(file))
}

const checkMove = () => false

const onDraggableStart = () => {
  document.body.classList.add("drag-inside-body")
}

const onDraggableEnd = () => {
  document.body.classList.remove("drag-inside-body")
}

const onClean = () => {
  newFiles.value = []
  uploadButton.value?.clear()
}

const deleteFile = (file: FileMetaData) => {
  emit("deleteFile", file)
}

const onClick = () => {
  onClean()
}

const updatedFiles = (payload: VUFile[]) => {
  if (!payload.length) return

  emit("upload", payload)
  nextTick(onClean)
}

const showDeleted = (value: boolean) => {
  emit("showDeleted", value)
}

const scroll = () => {
  if (wait.is("getFiles")) return

  emit("onScroll")
}
</script>
